import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Plus, X, Loader2, ImagePlus } from "lucide-react";

export const Field = ({ label, required, hint, children }) => (
  <div>
    <label className={ui.label}>{label}{required && <span className="text-rose-400"> *</span>}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const Section = ({ title, children }) => (
  <div className={`${ui.card} p-6 space-y-4`}>
    <h2 className={ui.sectionHeader}>{title}</h2>
    {children}
  </div>
);

export function ServicesEditor({ value, onChange, testid = "services" }) {
  const set = (i, k, v) => onChange(value.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      {value.map((s, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input data-testid={`${testid}-name-${i}`} className={ui.input} placeholder="Service name" value={s.name} onChange={(e) => set(i, "name", e.target.value)} />
          <input data-testid={`${testid}-desc-${i}`} className={ui.input} placeholder="Description (optional)" value={s.description} onChange={(e) => set(i, "description", e.target.value)} />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-2 text-slate-500 hover:text-rose-300"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...value, { name: "", description: "" }])} className={ui.btnGhost}><Plus className="w-4 h-4" /> Add service</button>
    </div>
  );
}

export function ChipInput({ value, onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => {
    const parts = v.split(",").map((x) => x.trim()).filter(Boolean);
    if (parts.length) onChange(Array.from(new Set([...value, ...parts])));
    setV("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input data-testid={`${testid}-input`} className={ui.input} placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button type="button" data-testid={`${testid}-add`} onClick={add} className={ui.btnGhost}><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {value.map((a) => (
          <span key={a} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-800 text-xs text-slate-200">
            {a}<button type="button" onClick={() => onChange(value.filter((x) => x !== a))}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function PairEditor({ value, onChange, a, b, aLabel, bLabel, addLabel, testid }) {
  const set = (i, k, v) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      {value.map((x, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input className={ui.input} placeholder={aLabel} value={x[a]} onChange={(e) => set(i, a, e.target.value)} />
          <input className={ui.input} placeholder={bLabel} value={x[b]} onChange={(e) => set(i, b, e.target.value)} />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-2 text-slate-500 hover:text-rose-300"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...value, { [a]: "", [b]: "" }])} className={ui.btnGhost}><Plus className="w-4 h-4" /> {addLabel}</button>
    </div>
  );
}

const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

export function ImageUpload({ value, onChange, multiple, testid }) {
  const [busy, setBusy] = useState(false);
  const list = multiple ? value : (value ? [value] : []);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const { data } = await api.post("/national/assets", { data_url: await readFile(f) });
        urls.push(data.url);
      }
      onChange(multiple ? [...value, ...urls].slice(0, 8) : urls[0]);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      {list.map((u) => (
        <div key={u} className="relative">
          <img src={u} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-800 bg-white" />
          <button type="button" onClick={() => onChange(multiple ? value.filter((x) => x !== u) : "")} className="absolute -top-2 -right-2 bg-slate-900 rounded-full p-1 border border-slate-700"><X className="w-3 h-3" /></button>
        </div>
      ))}
      {(multiple || !list.length) && (
        <label className={`${ui.btnGhost} cursor-pointer`}>
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <ImagePlus className="w-4 h-4" />} Upload
          <input data-testid={`${testid}-file`} type="file" accept="image/png,image/jpeg,image/webp" multiple={multiple} className="hidden" onChange={pick} />
        </label>
      )}
    </div>
  );
}

const SOCIALS = ["facebook", "instagram", "linkedin", "x", "youtube", "tiktok"];

export function SocialFields({ value, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {SOCIALS.map((k) => (
        <input key={k} data-testid={`social-${k}`} className={ui.input} placeholder={`${k[0].toUpperCase() + k.slice(1)} URL`}
          value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} />
      ))}
    </div>
  );
}
